/**
 * Helper function to handle unexpected cases and throw if reached.
 *
 * @param error - The error to throw
 */
export function throwError(error: Error | string): never {
  throw error instanceof Error ? error : new Error(error);
}

/**
 * Enforce exhaustive handling of values at compile time.
 *
 * @param value - A value that should be impossible here (typed as `never`).
 *
 * @throws {Error} Always throws with the unexpected value.
 *
 * @example switch statement
 * ```ts
 * type Status = "idle" | "loading";
 *
 * function getLabel(status: Status): string {
 *   switch (status) {
 *     case "idle":
 *       return "Idle";
 *     case "loading":
 *       return "Loading";
 *     default:
 *       return assertNever(status);
 *   }
 * }
 * ```
 * @example if else statement
 * ```ts
 * type Status = "idle" | "loading";
 *
 * function getLabel(status: Status): string {
 *   if (status === "idle") {
 *     return "Idle";
 *   } else if (status === "loading") {
 *     return "Loading";
 *   } else {
 *     return assertNever(status);
 *   }
 * }
 * ```
 * @example if statements
 * ```ts
 * type Status = "idle" | "loading";
 *
 * function getLabel(status: Status): string {
 *   if (status === "idle") {
 *     return "Idle";
 *   }
 *
 *   if (status === "loading") {
 *     return "Loading";
 *   }
 *
 *   return assertNever(status);
 * }
 * ```
 */
export function assertNever(value: never): never {
  throw new Error(`Unexpected value: '${value}'`);
}

export class ErrorWithData extends Error {
  private errorData?: Record<string, any>;
  constructor(message?: string, errorData?: Record<string, any>) {
    super(message);
    this.errorData = errorData;
  }
}
